import { useAuth0 } from '@auth0/auth0-react';

/**
 * Global error handler for API responses
 * Handles 401/403 authentication errors globally
 */
export function createGlobalErrorHandler() {
  return {
    post: async (context: { response: Response }) => {
      const { response } = context;

      if (response.status === 401 || response.status === 403) {
        if (
          typeof window !== 'undefined' &&
          (window.location.pathname === '/unauthorized' ||
            window.location.href.includes('/unauthorized'))
        ) {
          console.warn(
            'Authentication error on unauthorized page, skipping redirect to prevent loop'
          );
          return response;
        }

        // Only redirect for 403 with specific IP whitelist validation error message
        if (response.status === 403) {
          try {
            const clonedResponse = response.clone();
            const text = await clonedResponse.text();
            const isIPWhitelistError =
              text === 'IP address not whitelisted for organization';

            if (isIPWhitelistError) {
              if (typeof window !== 'undefined') {
                localStorage.removeItem('auth0.is.authenticated');
                localStorage.removeItem('auth0.cache');
              }
              window.location.href = `/unauthorized?error=ip-restricted`;
            }
            // All other errors (including other 403s and 401s) pass through
          } catch (e) {
            console.warn(
              'Could not parse error response, letting error pass through:',
              e
            );
          }
        }
        // All other status codes and non-IP-whitelist errors pass through

        return response;
      }

      return response;
    }
  };
}

/**
 * Hook to get the global error handler
 * This can be used in components that need to handle auth errors
 */
export function useGlobalErrorHandler() {
  const { logout } = useAuth0();

  const handleAuthError = () => {
    // Clear Auth0 session
    logout({
      returnTo: window.location.origin
    });
  };

  return { handleAuthError };
}
